<template>
  <div :class="['product-price', cssClasses, defaultPriceClass, authenticatedUserPriceClass, campaignPriceClass]">
    <!-- Both VAT figures per price, the chosen one first and larger (OC-3012). On the product page
         an agreement price also gets its second figure; reference prices (ordinary, list) get it
         only when there is no agreement price - as in the designs. -->
    <div class="product-price__main">
      <template v-if="!isProduct">
        <oie-product-price-group
          main
          :heading="tStatic('fromPriceLabel')"
          :price-ex-vat="lowestPrice.exVat"
          :price-inc-vat="lowestPrice.incVat"
          :show-other="!hasAgreementPrices"></oie-product-price-group>
        <oie-product-price-group
          v-if="hasAgreementPrices"
          :heading="tStatic('listPriceLabel')"
          :price-ex-vat="lowestListPrice.exVat"
          :price-inc-vat="lowestListPrice.incVat"
          :show-other="false"></oie-product-price-group>
      </template>

      <template v-else>
        <template v-if="isCampaignPrice">
          <oie-product-price-group
            main
            :heading="tStatic('campaignPrice')"
            :price-ex-vat="campaignPrice.exVat"
            :price-inc-vat="campaignPrice.incVat"></oie-product-price-group>
          <oie-product-price-group
            :heading="tStatic('ordinaryPriceLabel')"
            :price-ex-vat="customerPrice.exVat"
            :price-inc-vat="customerPrice.incVat"
            :show-other="!hasAgreementPrice"></oie-product-price-group>
        </template>

        <div v-else :data-pw="'price-' + priceModel.productId">
          <oie-product-price-group
            main
            :price-ex-vat="customerPrice.exVat"
            :price-inc-vat="customerPrice.incVat"></oie-product-price-group>
        </div>

        <oie-product-price-group
          v-if="hasAgreementPrice"
          :heading="tStatic('listPriceLabel')"
          :price-ex-vat="listPrice.exVat"
          :price-inc-vat="listPrice.incVat"
          :show-other="false"></oie-product-price-group>
      </template>
    </div>

    <oie-product-bracket-prices :bracket-prices="bracketPrices"></oie-product-bracket-prices>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

// A price both ways. Falls back to the single switch-following figure when the separate ones are
// missing, which then shows as one figure with no second line.
function pricePair(exVat, incVat, legacy) {
  if (exVat === undefined || exVat === null || incVat === undefined || incVat === null) {
    const value = legacy === undefined || legacy === null ? 0 : Number(legacy)
    return { exVat: value, incVat: value }
  }
  return { exVat: Number(exVat), incVat: Number(incVat) }
}

function differs(a, b) {
  return Math.abs((a || 0) - (b || 0)) >= 0.005
}

export default {
  props: {
    priceModel: {
      type: Object,
      required: true
    },
    isProduct: {
      type: Boolean,
      required: true
    },
    product: {
      type: Object,
      required: true
    }

  },
  data() {
    const m = this.priceModel
    return {
      listPrice: pricePair(m.listPriceExVat, m.listPriceIncVat, m.listPrice),
      lowestPrice: pricePair(m.lowestPriceExVat, m.lowestPriceIncVat, m.lowestPrice),
      lowestListPrice: pricePair(m.lowestListPriceExVat, m.lowestListPriceIncVat, m.lowestPrice),
      customerPrice: pricePair(m.customerPriceExVat, m.customerPriceIncVat, m.customerPrice),
      campaignPrice: pricePair(m.campaignPriceExVat, m.campaignPriceIncVat, m.campaignPrice),
      isCampaignPrice: m.isCampaignPrice,
      bracketPrices: m.bracketPrices
    }
  },
  computed: {
    ...mapGetters('ui', ['useVat']),
    ...mapGetters('commerce', ['currentProduct']),
    // The customer pays an agreement price of their own rather than the list price.
    hasAgreementPrice() {
      return this.isProduct && differs(this.listPrice.exVat, this.customerPrice.exVat)
    },
    // The same for a product with several variants, judged on the lowest prices. Only a signed-in
    // customer can have agreement prices, which keeps a campaign on one variant from looking like one.
    hasAgreementPrices() {
      return this.isAuthenticated && differs(this.lowestListPrice.exVat, this.lowestPrice.exVat)
    },
    isAuthenticated() {
      return this.clientModel.isAuthenticated
    },
    defaultPriceClass() {
      return !this.isAuthenticated && !this.isCampaignPrice && 'product-price--default'
    },
    authenticatedUserPriceClass() {
      return this.isAuthenticated && !this.isCampaignPrice && 'product-price--authenticated-user'
    },
    campaignPriceClass() {
      return this.isCampaignPrice && 'product-price--campaign'
    }
  },
  watch: {
    currentProduct: {
      deep: true,
      handler(value) {
        if(value.id === this.priceModel.productId) {
          this.lowestPrice = pricePair(value.lowestPriceExVat, value.lowestPriceIncVat, value.lowestPrice)
          this.lowestListPrice = pricePair(value.lowestListPriceExVat, value.lowestListPriceIncVat, value.lowestPrice)
          const price = _.find(value.items, item => item.id === this.clientModel.selectedItem)
          if(price && price.customerPrice) {
            const dto = price.customerPrice
            this.customerPrice = pricePair(
              dto.customerPriceExVat !== undefined ? dto.customerPriceExVat : dto.listPriceExVat,
              dto.customerPriceIncVat !== undefined ? dto.customerPriceIncVat : dto.listPriceIncVat,
              dto.customerPrice !== undefined ? dto.customerPrice : dto.listPrice)
            this.campaignPrice = pricePair(dto.campaignPriceExVat, dto.campaignPriceIncVat, dto.campaignPrice)
            this.isCampaignPrice = dto.campaignPrice !== undefined && dto.campaignPrice !== null
            this.bracketPrices = dto.bracketPrices
            this.listPrice = pricePair(dto.listPriceExVat, dto.listPriceIncVat, dto.listPrice)
          }
        }
      }
    }
  }

}
</script>
